<!--
  Map builder room admin screen.
  @author crm4f
-->

<template>
    
  <div class="roomAdminContainer">
    <div class="roomOptionsContainer">

      <div class="btn-group" role="group" aria-label="newItemOptions">
        <button type="button" :disabled="currentRoom == null || currentRoom?.roomId == null || currentRoom?.roomName == null || currentRoom?.floor == null" class="btn btn-primary active btn-sm" @click="saveCurrentRoom()">Save</button>
        <button type="button" :disabled="currentRoom == null" class="btn btn-primary active btn-sm" @click="clearCurrentRoom()">Cancel</button>
        <button type="button" :disabled="currentRoom == null" class="btn btn-primary active btn-sm" @click="deleteCurrentRoom()">Delete</button>
      </div>

      <!-- BUILDING SELECT -->
      <div class="optionRow">
        <div>
          <label for="building">Building:</label>
        </div>
        <select 
          @change="currentRoom = null; getRooms()"
          id="building"
          label="building"
          class="form-control-sm" 
          v-model="currentBuilding">
          <option v-for="(building,i) in buildings" :value="building" :key="i">{{ building.buildingName }}</option>
        </select>
        <br>
      </div>

      <!-- NEW ROOM -->
      <div v-if="currentBuilding !== null">
        <div class="optionRow">
          <button 
            :disabled="currentBuilding == null" 
            :class="{'inputDisabled' :currentBuilding == null}" 
            style="width:auto !important" 
            class="btn btn-primary active btn-sm" 
            @click="createNewRoom()">
            New Room
          </button>
          <select 
          @change="roomChanged()"
            :disabled="currentRoom?.newish"  
            :class="{'inputDisabled' : currentRoom?.newish }"
            class="form-control-sm" 
            v-model="currentRoom">
            <option v-for="(room,i) in rooms" 
              :value="room" 
              :key="i">
              {{ room.roomName }}
            </option>
          </select>
          <br>
        </div>
      </div>

      <!-- ROOM ID -->
      <div v-if="currentRoom !== null">
        <div class="optionRow">
          <div>
            <label for="id">Id:</label>
          </div>
          <input 
            id="id"
            :disabled="currentRoom == null" 
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm" 
            v-model="currentRoom.roomId"
            label="id"
            :placeholder="currentBuilding.buildingId + '...'"
            required
          />
          <br>
        </div>

        <!-- ROOM NAME -->
        <div class="optionRow">
          <div>
            <label for="name">Room:</label>
          </div>
          <input 
            id="name"
            :disabled="currentRoom == null"
            :class="{'inputDisabled' :currentRoom == null}"
            class="form-control-sm"
            v-model="currentRoom.roomName"
            label="name"
            required
          />
          <br>
        </div>

        <!-- LOCATION -->
        <div class="optionRow">
          <div>
            <span>Location:</span>
          </div>
          <div class="btn-group" role="group" aria-label="roomLocationOptions">
            <button 
              @click="addInternalRoom()"
              :disabled="currentRoom == null" 
              type="button" 
              class="btn btn-primary active btn-sm">
              Add
            </button>
          </div>
          <br>
        </div>

        <!-- SHAPE -->
        <div class="optionRow">
          <div>
            <span>Shape:</span>
          </div>
          <div class="btn-group" role="group" aria-label="roomShapeOptions">
            <button 
              @click="editRoomShape()"
              v-if="!drawingMode"
              :disabled="currentRoom?.shape == null" 
              type="button" 
              class="btn btn-primary active btn-sm">
              Edit
            </button>
            <button 
              @click="lockRoomShape()"
              v-if="drawingMode"
              :disabled="currentRoom?.shape == null" 
              type="button"
              class="btn btn-primary active btn-sm">
              Lock
            </button>
            <button 
              @click="clearRoomShape()"
              :disabled="currentRoom?.shape == null" 
              type="button"
              class="btn btn-primary active btn-sm">
              Clear
            </button>
          </div>
          <br>
        </div>

        <!-- SHOW LABEL AT ZOOM-->
        <div class="optionRow">
          <div>
            <label for="labelAtZoom">Show Label at Zoom:</label>
          </div>
          <input 
            id="labelAtZoom"
            :disabled="currentRoom == null" 
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm" 
            label="labelAtZoom" 
            v-model="currentRoom.showLabelAtZoom"
            type="number"/>
          <br>
        </div>
        
        <hr>

        <!-- FLOOR -->
        <div class="optionRow">
          <div>
            <label for="route">Floor:</label>
          </div>
          <input 
            id="floor"
            :disabled="currentRoom == null" 
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm" 
            label="floor" 
            v-model="currentRoom.floor"
            type="number"
            required
            />
          <br>
        </div>

        <!-- ENTRANCE -->
        <div class="optionRow">
          <div>
            <label for="entrance">Entrance:</label>
          </div>
          <select
            id="entrance"
            :disabled="currentRoom == null"
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm"
            v-model="currentRoom.entrance" 
            label="entrance">
            <option v-for="(entrance,i) in entrances" 
              :value="entrance" 
              :key="i"
              >
              {{entrance.featureName}}
            </option>
          </select>
          <br>
        </div>

        <hr>

        <!-- DIRECTIONS -->
        <div class="optionRow">
          <div>
            <label for="dirs">Directions:</label>
          </div>
          <textarea
            id="dirs"
            :disabled="currentRoom == null"
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm"
            v-model="currentRoom.directions"  
            label="dirs"
          />
          <br>
        </div>

        <!-- ACCESS URL -->
        <div class="optionRow">
          <div>
            <label for="accessUrl">Access URL:</label>
          </div>
          <input 
            id="accessUrl"
            :disabled="currentRoom == null" 
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm" 
            label="accessUrl" 
            v-model="currentRoom.accessUrl" 
          />
          <br>
        </div>

        <!-- DISPLAY ORDER -->
        <div class="optionRow">
          <div>
            <label for="displayOrder">Display Order:</label>
          </div>
          <input 
            id="displayOrder"
            :disabled="currentRoom == null" 
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm" 
            label="displayOrder" 
            v-model="currentRoom.displayOrder" 
            type="number"
          />
          <br>
        </div>

        <!-- POPUP STUDY ROOM -->
        <div class="optionRow">
          <div>
            <label for="r2r">Popup study room?</label>
          </div>
          <input 
            id="r2r"
            :disabled="currentRoom == null" 
            :class="{'inputDisabled' :currentRoom == null}" 
            class="form-control-sm" 
            label="r2r" 
            v-model="currentRoom.popupStudyRoom" 
            type="checkbox"
          />
          <br>
        </div>

      </div>

      <div style="color: #003865; margin-top: 25%" v-else>
        <h1 class="display-5">Select or create a room to begin editing...</h1>
        <font-awesome-icon style="font-size: 50px;" icon="fa-solid fa-building-circle-exclamation" />
      </div>

    </div>

    <div class="gmapContainer">
      <GMapMap
      :center='DEFAULT_CAMPUS_LATLNG'
      :zoom='17'
      class='mapCanvas tallMap'
      id="campusMap"
      ref='mapRef'
      style="width: 100%; height: 100vh"
      :options='{
        styles: getStyles(),
        fullscreenControl: false,
        mapTypeControl: false,
        mapTypeControlOptions: {
          position: google && google.maps.ControlPosition.TOP_LEFT,
          style: google && google.maps.MapTypeControlStyle.DROPDOWN_MENU
        },
        streetViewControl: true,
        zoomControl: true
      }'>

        <!-- ROOM ENTRANCE MARKER -->
        <GMapMarker v-if="currentRoom?.entrance !== null"
          ref="roomEntranceMarker"
          :key="index"
          :position="determinePos(currentRoom)"
          :clickable="true"
          :draggable="false"
          @click="openRoomEntranceInfoWindow(currentRoom.entrance.featureId)"
        >
          <GMapInfoWindow @closeclick="openRoomEntranceInfoWindow(null)" :closeclick="true" :opened="currentRoom.entrance.featureId == openedRoomEntranceInfoWindow">
            <div>
              <strong>{{ currentRoom.entrance.featureName }}</strong>
              <br/>
              {{ (currentRoom.directions != null ? currentRoom.directions : '') }}
            </div>
          </GMapInfoWindow>
        </GMapMarker>

        <!-- INTERNAL ROOM MARKER -->
        <GMapMarker v-if="currentRoom?.lat != null && currentRoom?.lng != null"
          ref="internalRoomMarker"
          :key="index"
          :icon="{
            url: require('@/assets/secondary_marker.png'),
            scaledSize: {width: 26, height: 38},
          }"
          :position="{lat: Number(currentRoom.lat), lng: Number(currentRoom.lng)}"
          :clickable="true"
          :draggable="true"
          @click="openInternalRoomInfoWindow(currentRoom.roomId)"
          @dragend="updateInternalRoomPos($event)"
        >
          <GMapInfoWindow @closeclick="openInternalRoomInfoWindow(null)" :closeclick="true" :opened="currentRoom.roomId == openedInternalRoomInfoWindow">
            <div>
              <strong>{{ currentRoom.roomName }}</strong>
              <br/>
              {{ (currentRoom.directions != null ? currentRoom.directions : '') }}
            </div>
          </GMapInfoWindow>
        </GMapMarker>


      </GMapMap>
    </div>

  </div>
</template>
  
<script setup>
import { onBeforeUnmount, onMounted, ref, shallowRef, toRaw } from 'vue'
import { DrawingBoard } from './DrawingBoard.js'
import axios from 'axios'

const DEFAULT_CAMPUS_LATLNG = {lat: 55.872336, lng: -4.288118}

const buildings = ref(null)
const rooms = ref(null)
const currentBuilding = ref(null)
const currentRoom = ref(null)
const openedRoomEntranceInfoWindow = ref(null)
const openedInternalRoomInfoWindow = ref(null)
const entrances = ref(null)
const drawingMode = ref(false)

const mapRef = ref(null)
const map = shallowRef(null)
const google = shallowRef(null)

let drawingBoard = null

onMounted(() => {

  getBuildings()
  getEntrances()

  mapRef.value.$gmapApiPromiseLazy().then((gmapApi) => {
    console.log('gmapApi promise fulfilled')
    google.value = gmapApi
    return mapRef.value.$mapPromise
  }).then((mapResponse) => {
    const gmap = toRaw(mapResponse)
    map.value = gmap
    google.value.maps.event.addListenerOnce(gmap, 'idle', () => {
      drawingBoard = new DrawingBoard(gmap, (path) => {
        if (currentRoom.value !== null) {
          currentRoom.value.shape = path
        }
      }, google.value)
      if (currentRoom.value !== null) {
        roomChanged()
      }
    })
  })
})

onBeforeUnmount(() => {
  if (drawingBoard !== null) {
    drawingBoard.destroy()
  }
})

/**
   * Method to open/track info windows for the room entrance markers.
*/
function openRoomEntranceInfoWindow (entranceId) { 
  openedRoomEntranceInfoWindow.value = entranceId
}

/**
   * Method to open/track info windows for the internal room markers.
*/
function openInternalRoomInfoWindow (roomId) {
  openedInternalRoomInfoWindow.value = roomId
}

/**
 * Method to update the currentRoom position, once the user has finished dragging the marker. 
*/
function updateInternalRoomPos (event) {
  currentRoom.value.lat = event.latLng.lat()
  currentRoom.value.lng = event.latLng.lng()
}

/**
  * Room change handler.
  * Resets the form and the map then triggers route and colour changes.
*/
function roomChanged () {
  drawingMode.value = false

  if (drawingBoard === null) {
    return
  }

  drawingBoard.clear()
  if (currentBuilding.value === null || currentRoom.value === null || currentRoom.value.newish) {
    drawingBoard.lock()
    return
  }

  if (currentRoom.value.entrance) {
    map.value.panTo({ lat: Number(currentRoom.value.entrance.lat), lng: Number(currentRoom.value.entrance.lng) })
  }
  if (currentRoom.value.shape == null) {
    drawingBoard.showTools()
  } else {
    drawingBoard.draw(currentRoom.value.shape)
  }
}

function addInternalRoom () {
  currentRoom.value.lat = DEFAULT_CAMPUS_LATLNG.lat
  currentRoom.value.lng = DEFAULT_CAMPUS_LATLNG.lng
  map.value.panTo(DEFAULT_CAMPUS_LATLNG)
}

function createNewRoom () {
  axios.post("map/admin/buildings/" + currentBuilding.value.buildingId + "/rooms")
  .then((response) => {
    currentRoom.value = response.data
    currentRoom.value.newish = true
    rooms.value.push(currentRoom.value)
    roomChanged()
    if (drawingBoard !== null) {
      drawingBoard.showTools()
    }
  })   
}

/**
  * Update the room in the database.
*/
function saveCurrentRoom () {
  if (currentRoom.value.roomId.indexOf(currentBuilding.value.buildingId.substring(0, 3)) == 0) {
    axios.put("map/admin/buildings/" + currentBuilding.value.buildingId + "/rooms", currentRoom.value)
    .then((response) => {
      if (response.status == 200) {
        alert('Update successful.')
      } else if (response.status == 401 || response.status == 403) {
        alert('You are not authenticated. Check console for details.')
      } else {
        alert('An error occurred. Check console for details')
      }
      currentRoom.value.newish = false
    })  
  } else {
    alert('The room id must begin with the building id.')
  }
}

/**
  * Cancel changes and trigger a room change event.
*/
function clearCurrentRoom () {
  if(confirm("Cancel current room edit?")) {
    currentRoom.value = null
    roomChanged()
    getRooms()
  }  
}

/**
  * Delete the room from the database and trigger a room change event.
*/
function deleteCurrentRoom () {
  if(confirm("Are you sure?")) {
    axios.delete("map/admin/buildings/" + currentBuilding.value.buildingId + "/rooms/" + currentRoom.value.roomId)
      .then((response) => {
        if (response.status == 200) {
          alert('Room deleted successfully.')
        } else if (response.status == 401 || response.status == 403) {
          alert('You are not authenticated. Check console for details.')
        } else {
          alert('Something unexpected happened. Check console for details')
        }
        currentRoom.value = null
        roomChanged()
        getRooms()
      })    
  }      
}

/**
  * Make room overlay editable.
*/
function editRoomShape () {
  drawingMode.value = true
  if (drawingBoard !== null) {
    drawingBoard.edit()
  }
}

/**
  * Clear room overlay and reset drawing manager.
*/
function clearRoomShape () {
  if(confirm("Are you sure?")) {
    drawingMode.value = false
    if (drawingBoard !== null) {
      drawingBoard.clear()
      drawingBoard.showTools()
    }
    currentRoom.value.shape = null
  }
}

/**
  * Lock the room overlay.
*/
function lockRoomShape () {
  drawingMode.value = false
  if (drawingBoard !== null) {
    drawingBoard.lock()
  }
}


function getBuildings() {
  axios.get('map/admin/buildings',{withCredentials:false})
  .then(response => {
    buildings.value = response.data
  })
}

function getRooms() {
  axios.get('map/admin/buildings/' + currentBuilding.value.buildingId + '/rooms',{withCredentials:false})
  .then(response => {
    rooms.value = response.data
  })
}

function getEntrances() {
  axios.get('map/admin/features/entrances',{withCredentials:false})
  .then(response => {
      entrances.value = response.data
  })
}

/**
 * Method to evaulate what latLng value to supply the Gmap.
*/
function determinePos(location) {
  var loc = toRaw(location)
  if ((loc?.entrance?.lat != null) && (loc?.entrance?.lng != null)) {
    return {lat: Number(loc.entrance.lat), lng: Number(loc.entrance.lng)}
  } else {
    return DEFAULT_CAMPUS_LATLNG
  }
}

/**
* Styles config for the Gmap plugin. 
*/
function getStyles () {
  return [
    { featureType: 'road', elementType: 'geometry', stylers: [ { lightness: 100 }, { visibility: 'simplified' } ] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [ { color: '#575656' } ] },
    { featureType: 'road', elementType: 'labels.text.stroke', stylers: [ { color: '#FFFFFF' } ] },
    { featureType: 'poi', elementType: 'labels', stylers: [ { visibility: 'off' } ] } 
  ]
}

</script>
  
<style scoped>

.roomAdminContainer {
  display: flex;
}

.gmapContainer {
  width: 100%;
  padding: 10px;
  height: fit-content;
}

.listHeader {
  color: #003865;
  font-weight: bold;
}

.hrhalfheight {
  width: 75%;
  margin: auto;
}

.feature-list {
  margin-bottom: 15px;
}

.inputDisabled {
  cursor: not-allowed;
}

.showFeatures, .showFeatures button {
  width: 100%;
}

.featuresContainer {
  margin-top: 15px;
}

.roomOptionsContainer {
  padding: 25px 0px 10px 10px;
  width: 60%;
}

.optionRow {
  display: flex;
  margin: 10px 0px 0px 5px;
  align-items: center;
  justify-content: space-between;
}

.optionRow > :nth-child(1) {
  margin-right: 15px;
  width: 25%;
  text-align: left;
  word-break: break-word;
  font-size: 13px;
  font-weight: bold;
}

.optionRow > :nth-child(2) {
  flex: 1;
  width: 100% !important;
}

</style>